"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.KatalMonitoringDriver = exports.KatalMetricDomain = void 0;
const KatalMetricsDriver_1 = __importDefault(require("@amzn/katal-metrics/lib/driver/KatalMetricsDriver"));
/**
 * Metric Domain.
 */
var KatalMetricDomain;
(function (KatalMetricDomain) {
    KatalMetricDomain["PROD"] = "prod";
    KatalMetricDomain["TEST"] = "test";
})(KatalMetricDomain = exports.KatalMetricDomain || (exports.KatalMetricDomain = {}));
const defaultErrorHandler = (err) => {
    console.error('Error publishing metrics', err);
};
// This is based on value calculated with maximum allowed values for each field. Referenced from https://tiny.amazon.com/1k08tdtv0
// For string type metric, the value is 1057 by using JSON.stringify(<object>).length,
// Therefore by rounding off to 1000 it can accomodate upto 50 metrics at a time.
const DEFAULT_ALLOWED_PAYLOAD_SIZE = 50000; // 50KB
const DEFAULT_MAX_METRICS_PER_BATCH = 25;
const DEFAULT_BATCH_TIMEOUT_DURATION = 10000; // 10 Seconds
const DEFAULT_XHR_API_CALL_COUNT = 3;
const OPTIONS_DEFAULTS = {
    batchTimeoutDuration: DEFAULT_BATCH_TIMEOUT_DURATION,
    headers: {},
    logToConsole: false,
    maxItemsPerBatch: DEFAULT_MAX_METRICS_PER_BATCH,
    xhrWithCredentials: false,
    useXMLHttpRequest: false,
    domain: KatalMetricDomain.TEST,
    xhrApiTotalCallCount: DEFAULT_XHR_API_CALL_COUNT,
    allowedPayloadSize: DEFAULT_ALLOWED_PAYLOAD_SIZE,
    errorHandler: defaultErrorHandler,
    url: '',
};
class KatalMonitoringDriver extends KatalMetricsDriver_1.default {
    constructor(options) {
        super();
        /**
         * Sends request to backend and resets the current batch data.
         */
        this.sendDataToBackend = async () => {
            const metricsToSend = this.metricsAPIPayloadQueue;
            if (!metricsToSend.length) {
                return;
            }
            const metrics = {
                publishTimestamp: Date.now(),
                metrics: metricsToSend,
            };
            this.resetBatch();
            await this.callAPI(this.stringify(metrics));
        };
        /**
         * Call the error-handler supplied by the user when this object was constructed; if that is unset or itself throws
         * an exception, calls the default error handler as a fallback, which will just log the error to the console.
         *
         * @param err Error object to handle
         */
        this.combinedErrorHandler = (err) => {
            try {
                this.errorHandler(err);
                // Return to avoid falling through to default error handler
                return;
            }
            catch (nextErr) {
                console.error('Error handling an error', nextErr);
                // Fall through
            }
            // In case the passed error handler fails, call default handler.
            defaultErrorHandler(err);
        };
        const configWithDefaults = { ...OPTIONS_DEFAULTS, ...options };
        this.url = configWithDefaults.url;
        this.logToConsole = configWithDefaults.logToConsole;
        this.headers = configWithDefaults.headers;
        this.batchTimeoutDuration = configWithDefaults.batchTimeoutDuration;
        this.maxItemsPerBatch = configWithDefaults.maxItemsPerBatch;
        this.xhrWithCredentials = configWithDefaults.xhrWithCredentials;
        this.useXMLHttpRequest = configWithDefaults.useXMLHttpRequest;
        this.sushiDriver = configWithDefaults.metricsSushiDriver;
        this.metricsAPIPayloadQueue = [];
        this.currentBatchSize = 0;
        this.domain = configWithDefaults.domain;
        this.realm = configWithDefaults.realm;
        this.xhrApiTotalCallCount = configWithDefaults.xhrApiTotalCallCount;
        this.allowedPayloadSize = configWithDefaults.allowedPayloadSize;
        this.errorHandler = configWithDefaults.errorHandler;
        if (configWithDefaults.backendApiOverride &&
            typeof configWithDefaults.backendApiOverride === 'function') {
            this.callAPI = configWithDefaults.backendApiOverride;
        }
        else if (!this.url) {
            console.error('URL should be defined if no backend override is provided. No metrics will be published.');
        }
        this.registerUnloadAction(() => {
            this.sendDataToBackend();
        });
    }
    /**
     * Implementation of publish method to send metrics to backend.
     * @param metricObject Metric object containing metric name, value, etc.
     * @param metricContext Metric context containing site, serviceName, etc.
     */
    publish(metricObject, metricContext) {
        try {
            // This condition is to cover migration from existing sushi driver to new AWS driver without losing any metrics.
            if (this.sushiDriver) {
                this.sushiDriver.publish(metricObject, metricContext);
            }
            this.sendRequest(this.getMetricObjectAPIPayload(metricObject, metricContext));
        }
        catch (err) {
            this.combinedErrorHandler(err);
        }
    }
    /**
     * Create metric object from the passed params as per APIG Model.
     * @param metricObject Metric object containing metric name, value, etc.
     * @param metricContext Metric context containing site, serviceName, etc.
     * @returns Final Metric object data adhering to APIG schema.
     */
    getMetricObjectAPIPayload(metricObject, metricContext) {
        const contextFields = metricContext.getFields();
        return {
            /**
             * Below 2 fields (site & serviceName) are optional in KatalMetricsContextFields class but both are required in APIG Model - https://tiny.amazon.com/o1196gvl
             * KatalMetrics runs a validator before publishing - https://tiny.amazon.com/14ov0kg0c
             * We do handle empty values for below 2 fields in our backend lambda using same Regex as Cradle jobs - https://tiny.amazon.com/1dpio0sk0.
             * Lambda will publish invalid count metrics for each metric type - https://tiny.amazon.com/1eow6yg9w
             * CDK code also creates Dashboard for above invalid metrics - https://tiny.amazon.com/7mjqf3zm
             */
            site: contextFields.site || '',
            serviceName: contextFields.serviceName || '',
            actionId: contextFields.actionId,
            methodName: contextFields.methodName,
            metricKey: metricObject.name,
            metricType: metricObject.type,
            timestamp: Date.now(),
            isMonitor: metricObject.isMonitor || false,
            value: metricObject.value,
            domain: this.domain,
            realm: this.realm,
            dimensions: this.getMetricsDimension(contextFields.cloudWatchDimensions || []),
        };
    }
    /**
     * Transform the array of string metrics into key-value pair of dimensions.
     * @param dimensions Array of string metrics.
     * @returns Dimensions object.
     */
    getMetricsDimension(dimensions) {
        return dimensions.reduce((acc, cur) => ({ ...acc, [cur.name]: cur.value }), {});
    }
    /**
     * Add metrics to request queue and log it to console if logToConsole is true.
     * @param metricData Final metrics object data.
     */
    sendRequest(metricData) {
        this.addMetricsToRequestQueue(metricData);
        if (this.logToConsole) {
            console.log('Emitting metrics...', metricData);
        }
    }
    /**
     * Based on multiple conditions like payload or request queue size, either add passed metric to request queue or send to the backend.
     * @param metricData Final metrics object data.
     */
    addMetricsToRequestQueue(metricData) {
        const currentMetricDataSize = this.stringify(metricData).length;
        // Checking if adding current metric data is greater than allowed payload size.
        // If it's greater then send existing metric data payload to backend.
        if (this.currentBatchSize + currentMetricDataSize >
            this.allowedPayloadSize) {
            this.sendDataToBackend();
        }
        // Add current metric data size to existing batch size.
        this.currentBatchSize += currentMetricDataSize;
        // Add metric data to the request queue.
        this.metricsAPIPayloadQueue.push(metricData);
        // Check if adding new metric to queue is equal to max API batch size or allowed payload size,
        // if yes, then send metrics to backend else check if there is an existing timer, if not create a new one.
        if (this.metricsAPIPayloadQueue.length === this.maxItemsPerBatch ||
            this.currentBatchSize === this.allowedPayloadSize) {
            this.sendDataToBackend();
        }
        else if (!this.queueTimerId) {
            this.queueTimerId = window.setTimeout(this.sendDataToBackend, this.batchTimeoutDuration);
        }
    }
    /**
     * Resets current metric batch data.
     */
    resetBatch() {
        this.metricsAPIPayloadQueue = [];
        this.currentBatchSize = 0;
        window.clearTimeout(this.queueTimerId);
        this.queueTimerId = undefined;
    }
    /**
     * Check if it needs to call 'navigator.sendBeacon' or XHR.
     * @returns Boolean flag
     */
    doCallBeacon() {
        return !(Object.keys(this.headers).length || this.useXMLHttpRequest);
    }
    /**
     * Make backend API call either using sendBeacon or XHR.
     * @param dataString Stringified POST data for API.
     */
    async callAPI(dataString) {
        // If the amount of data to be queued exceeds the user agent limit,
        // "navigator.sendBeacon" method returns false; a return value of true implies the browser has queued the data for transfer.
        // https://www.w3.org/TR/beacon/#return-value
        if (this.doCallBeacon() && navigator.sendBeacon(this.url, dataString)) {
            console.info('The browser has successfully queued the data for transfer...');
        }
        else {
            await this.xhrRetry(dataString).catch((err) => {
                this.combinedErrorHandler(err);
            });
        }
    }
    /**
     * xhr call to APIG with retry mechanism.
     * @param dataString Stringified POST data for API.
     * @param retryCount Retry count.
     */
    async xhrRetry(dataString, retryCount = 1) {
        try {
            return await this.callXhr(dataString);
        }
        catch (err) {
            if (retryCount === this.xhrApiTotalCallCount) {
                console.debug(`APIG call failed for ${this.xhrApiTotalCallCount} times.`, err);
                throw err;
            }
            console.debug(`Retrying API call for time ${retryCount}`);
            return this.xhrRetry(dataString, retryCount + 1);
        }
    }
    /**
     * Make an XHR call to APIG endpoint with passed data.
     * @param dataString Stringified POST data for API.
     */
    callXhr(dataString) {
        return new Promise((resolve, reject) => {
            // Standard XHR request
            const xhr = new XMLHttpRequest();
            // xhr request is set to async.
            // In case this needs to be changed to sync, it should be noted that xhr.withCredentials with sync request is not supported since Firefox 11.
            // See https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/Using_XMLHttpRequest#browser_compatibility
            xhr.open('POST', this.url, true);
            if (this.xhrWithCredentials) {
                xhr.withCredentials = this.xhrWithCredentials;
            }
            Object.entries(this.headers).forEach(([headerName, headerValue]) => {
                xhr.setRequestHeader(headerName, headerValue);
            });
            // When the request loads, check whether it was successful
            xhr.onload = () => {
                if (xhr.status === 204) {
                    // If successful, resolve the promise.
                    resolve();
                }
                else if (xhr.status >= 400 && xhr.status < 500) {
                    // In case of 4XX errors, resolve the promise to avoid calling APIG again as it's an input validation error.
                    console.debug(`Validation error occured while calling API, ${xhr.response}`);
                    resolve();
                }
                else {
                    // If it fails, reject the promise with a error status.
                    console.debug(`API called failed due to ${xhr.response}`);
                    reject(Error('Error calling APIG endpoint' + xhr.status));
                }
            };
            xhr.onerror = () => {
                // This is likely a network error, so reject the promise with a proper message.
                reject(Error('There was a network error.'));
            };
            // Send the request
            xhr.send(dataString);
        });
    }
    /**
     * Wrapper around JSON.stringy to handle any exception.
     * @param data Object to be stringified.
     * @returns Stringified object or throw err in case of exception.
     */
    stringify(data) {
        try {
            return JSON.stringify(data);
        }
        catch (err) {
            console.error('Unable to stringify data', err);
            throw err;
        }
    }
    /**
     * Cover all of the unload cases:
     * https://www.igvita.com/2015/11/20/dont-lose-user-and-app-state-use-page-visibility/
     */
    registerUnloadAction(callback) {
        this.registerEvent('pagehide', callback);
        this.registerEvent('beforeunload', callback);
        this.registerEvent('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                callback();
            }
        });
    }
    /**
     * Add passed event listener to window.
     * @param event Event listener name.
     * @param cb Callback to be called.
     */
    registerEvent(event, cb) {
        window.addEventListener(event, cb);
    }
}
exports.KatalMonitoringDriver = KatalMonitoringDriver;
